"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

const SESSION_SENT_KEY = "sucom_session_beacon_at";
const SESSION_WINDOW_MS = 30 * 60 * 1000;

function lastSentAt() {
  try {
    return Number(window.localStorage.getItem(SESSION_SENT_KEY) ?? 0);
  } catch {
    return 0;
  }
}

function rememberSent() {
  try {
    window.localStorage.setItem(SESSION_SENT_KEY, String(Date.now()));
  } catch {
    // The server-side bucket still deduplicates privacy-mode browsers.
  }
}

function campaignValue(params: { get(name: string): string | null }, key: string) {
  const value = params.get(key)?.trim();
  return value && /^[a-z0-9_.-]{1,80}$/i.test(value) ? value.toLowerCase() : null;
}

/**
 * Records a first-party session only after the page was genuinely used:
 * either a trusted interaction or eight visible seconds. This deliberately
 * avoids counting crawlers, prefetches and instant bounces as active users.
 */
export function VisitorSessionBeacon() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (pathname === "/admin" || pathname.startsWith("/admin/")) return;
    if (document.visibilityState !== "visible") return;
    const previous = lastSentAt();
    if (Number.isFinite(previous) && Date.now() - previous < SESSION_WINDOW_MS) return;

    let sent = false;
    let visibleSince = Date.now();
    let timer: number | null = null;

    const send = (engagement: "interaction" | "visible-8s") => {
      if (sent || document.visibilityState !== "visible") return;
      sent = true;
      rememberSent();
      const payload = JSON.stringify({
        path: pathname,
        referrer: document.referrer || null,
        source: campaignValue(searchParams, "utm_source"),
        medium: campaignValue(searchParams, "utm_medium"),
        campaign: campaignValue(searchParams, "utm_campaign"),
        engagement,
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/analytics/session", new Blob([payload], { type: "application/json" }));
      } else {
        fetch("/api/analytics/session", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch(() => undefined);
      }
      cleanup();
    };

    const onInteraction = (event: Event) => {
      if (event.isTrusted) send("interaction");
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        visibleSince = Date.now();
        scheduleVisible();
      } else if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    };
    const scheduleVisible = () => {
      if (timer) window.clearTimeout(timer);
      const remaining = Math.max(0, 8_000 - (Date.now() - visibleSince));
      timer = window.setTimeout(() => send("visible-8s"), remaining);
    };
    const cleanup = () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("pointerdown", onInteraction);
      window.removeEventListener("keydown", onInteraction);
      window.removeEventListener("touchstart", onInteraction);
      document.removeEventListener("visibilitychange", onVisibility);
    };

    window.addEventListener("pointerdown", onInteraction, { passive: true });
    window.addEventListener("keydown", onInteraction);
    window.addEventListener("touchstart", onInteraction, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    scheduleVisible();
    return cleanup;
  }, [pathname, searchParams]);

  return null;
}
